import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const Redeem = () => {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('loading'); // loading, success, error, daily_limit
  const [authChecked, setAuthChecked] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [blogId, setBlogId] = useState(''); // Store blog ID for better error messages
  const processingRef = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { earnCoins } = useWallet();

  // Extract code and save it to localStorage if available
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeFromUrl = params.get('code');
    const blogIdFromUrl = params.get('blogId');
    
    console.log('Full URL:', window.location.href);
    console.log('Extracted code from URL:', codeFromUrl);
    
    if (codeFromUrl) {
      // Save code to localStorage for persistence during auth redirect
      localStorage.setItem('pendingRedeemCode', codeFromUrl);
    }
    
    if (blogIdFromUrl) {
      // Save blogId to use in error messages
      setBlogId(blogIdFromUrl);
      localStorage.setItem('pendingRedeemBlogId', blogIdFromUrl);
    }
  }, [location.search]);

  useEffect(() => {
    // Function to process the reward code
    const redeemCode = async (code, userId) => {
      // Prevent duplicate API calls
      if (processingRef.current) {
        console.log('Already processing a redemption, skipping duplicate call');
        return;
      }
      
      try {
        setIsProcessing(true);
        processingRef.current = true;
        
        console.log('Attempting to redeem code:', code);
        console.log('User ID:', userId);
        
        // Get blogId from URL or localStorage
        const params = new URLSearchParams(location.search);
        const blogIdFromUrl = params.get('blogId') || localStorage.getItem('pendingRedeemBlogId');
        const source = params.get('source');
        
        // Log the payload before sending
        const payload = { 
          code, 
          userId,
          blogId: blogIdFromUrl || undefined,
          source: source || undefined
        };
        console.log('Sending payload:', payload);
        
        // Call your API to redeem the code
        // Using the newly deployed endpoint - add the proper path
        const response = await fetch('https://redeemcode-vji7sqed4a-uc.a.run.app/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        console.log('API Response status:', response.status);
        const data = await response.json();
        console.log('API Response data:', data);

        if (response.ok) {
          setMessage('50 coins added to your wallet!');
          setStatus('success');
          // Clear the pending code
          localStorage.removeItem('pendingRedeemCode');
          localStorage.removeItem('pendingRedeemBlogId');
        } else {
          // Check for specific error messages
          if (data.message?.toLowerCase().includes('already claimed a google search reward for this blog today')) {
            const savedBlogId = localStorage.getItem('pendingRedeemBlogId') || blogId;
            setMessage(`You have already claimed a Google search reward for this blog today. Try finding a different blog through Google search!`);
            setStatus('daily_limit'); // Special status for daily limit errors
          } else if (data.error === 'already_claimed' || data.message?.toLowerCase().includes('already claimed')) {
            setMessage('You have already claimed this reward');
            setStatus('error');
          } else {
            setMessage(data.message || 'Invalid code');
            setStatus('error');
          }
        }
      } catch (error) {
        console.error('Error redeeming code:', error);
        setMessage('An error occurred while redeeming your code');
        setStatus('error');
      } finally {
        setIsProcessing(false);
        processingRef.current = false;
      }
    };

    // Add a timeout to give Firebase auth time to initialize
    const authCheckTimer = setTimeout(() => {
      setAuthChecked(true);
    }, 2000); // Wait 2 seconds before checking auth

    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, user => {
      if (user) {
        console.log('User is authenticated:', user.uid);
        
        // Get code from URL or localStorage
        const params = new URLSearchParams(location.search);
        let code = params.get('code');
        
        // If no code in URL, try from localStorage (for after login redirect)
        if (!code) {
          code = localStorage.getItem('pendingRedeemCode');
        }
        
        // If still no code, try from URL path
        if (!code) {
          const pathParts = window.location.pathname.split('/');
          const lastPart = pathParts[pathParts.length - 1];
          if (lastPart && lastPart !== 'redeem') {
            code = lastPart;
            console.log('Extracted code from path:', code);
          }
        }

        if (code && !isProcessing) {
          redeemCode(code, user.uid);
        } else if (!code) {
          setMessage('No reward code found');
          setStatus('error');
        }
      } else if (authChecked) {
        // Only redirect to login if we've given auth time to initialize
        console.log('User not authenticated, redirecting to login');
        // Save current location to redirect back after login
        navigate('/login?redirect=/redeem' + location.search);
      }
    });

    return () => {
      clearTimeout(authCheckTimer);
      unsubscribe();
    };
  }, [authChecked, location.search, navigate, earnCoins, isProcessing, blogId]);

  // Get time remaining until midnight for daily limit message
  const getTimeUntilMidnight = () => {
    const now = new Date();
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);
    
    const diffMs = midnight - now;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${diffHrs} hours and ${diffMins} minutes`;
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-md mx-auto mt-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
          {status === 'loading' && (
            <div className="py-8">
              <svg className="animate-spin h-10 w-10 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-4 text-gray-600 dark:text-gray-300">Processing your reward...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-green-500 dark:text-green-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Success!</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
              <button 
                onClick={() => navigate('/earn')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back to Earn Page
              </button>
            </div>
          )}
          
          {status === 'daily_limit' && (
            <div className="py-8">
              <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-amber-500 dark:text-amber-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Already Claimed</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4">{message}</p>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Each blog can be claimed once per day through Google search. Try a different blog or come back in <span className="font-medium">{getTimeUntilMidnight()}</span>.
              </p>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/earn')}
                  className="inline-flex w-full justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back to Earn Page
                </button>
                <button 
                  onClick={() => navigate('/browse')}
                  className="inline-flex w-full justify-center items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Browse Other Articles
                </button>
                <a 
                  href="https://www.google.com/search?q=site:uptodatefinance.com" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-full justify-center items-center px-4 py-2 border border-yellow-300 dark:border-yellow-600 text-sm font-medium rounded-md shadow-sm text-yellow-700 dark:text-yellow-200 bg-yellow-50 dark:bg-yellow-900/30 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path>
                    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path>
                    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path>
                    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path>
                  </svg>
                  Search for Another Blog
                </a>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="py-8">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="h-8 w-8 text-red-500 dark:text-red-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Error</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">{message}</p>
              <button 
                onClick={() => navigate('/earn')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Back to Earn Page
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Redeem; 